import axios from 'axios';

// Use relative path for production (served by same nginx)
// Use localhost:8080 for development
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' ? 'http://localhost:8080' : '/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('mjtech_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('mjtech_token');
      localStorage.removeItem('mjtech_user');
      window.location.reload();
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const auth = {
  login: (email, password) => api.post('/auth/login', { Username: email, Password: password }),
  register: (email, password, fullName) =>
    api.post('/auth/register', { Email: email, Password: password, FullName: fullName }),
  getCurrentUser: () => api.get('/auth/user'),
  logout: () => api.post('/auth/logout'),
};

// Products endpoints
export const products = {
  getAll: (params) => api.get('/api/v1/products', { params }),
  getFiltered: (params) => api.get('/api/v1/products/filtered', { params }),
  getById: (id) => api.get(`/api/v1/products/${id}`),
  create: (product) => api.post('/api/v1/products', product),
  update: (id, product) => api.put(`/api/v1/products/${id}`, product),
  delete: (id) => api.delete(`/api/v1/products/${id}`),
  updateStock: (id, branchName, stockData) => api.put(`/api/v1/products/${id}/stock/${branchName}`, stockData),
  transferStock: (id, transferData) => api.post(`/api/v1/products/${id}/stock/transfer`, transferData),
};

// Sales endpoints
export const sales = {
  create: (saleData) => api.post('/api/v1/sales', saleData),
  getAnalytics: () => api.get('/api/v1/sales/analytics'),
  findByReference: (reference) => api.get('/api/v1/sales/find-by-reference', { params: { reference } }),
  refund: (saleId, refundData) => api.put(`/api/v1/sales/${saleId}/refund`, refundData),
};

// Images endpoints
export const images = {
  getPreview: (id) => api.get(`/images/preview/${id}`),
};

// Branches endpoints
export const branches = {
  getAll: () => api.get('/api/v1/branches'),
};

// Lookup endpoints
export const lookup = {
  getQuaipo: (params) => api.get('/api/v1/lookup/quaipo', { params }),
  getQuaipoFilters: () => api.get('/api/v1/lookup/quaipo/filters'),
  createQuaipo: (data) => api.post('/api/v1/lookup/quaipo', data),
  updateQuaipo: (id, data) => api.put(`/api/v1/lookup/quaipo/${id}`, data),
  deleteQuaipo: (id) => api.delete(`/api/v1/lookup/quaipo/${id}`),
};

// Cache for dropdown data (brands, categories, and suppliers)
const dropdownCache = {
  brands: { data: null, timestamp: null, ttl: 5 * 60 * 1000 }, // 5 minutes TTL
  categories: { data: null, timestamp: null, ttl: 5 * 60 * 1000 },
  suppliers: { data: null, timestamp: null, ttl: 5 * 60 * 1000 }
};

// Helper function to check if cache is valid
const isCacheValid = (cache) => {
  return cache.data && cache.timestamp && (Date.now() - cache.timestamp) < cache.ttl;
};

// Brands and Categories with caching
export const brands = {
  // Get all brands (full data)
  getAll: () => api.get('/api/v1/brands'),

  // Get lightweight brands for dropdowns (cached)
  getLite: async () => {
    if (isCacheValid(dropdownCache.brands)) {
      return { data: dropdownCache.brands.data };
    }

    const response = await api.get('/api/v1/brands/lite');
    dropdownCache.brands.data = response.data;
    dropdownCache.brands.timestamp = Date.now();
    return response;
  }
};

export const categories = {
  // Get all categories (full data)
  getAll: () => api.get('/api/v1/categories'),

  // Get lightweight categories for dropdowns (cached)
  getLite: async () => {
    if (isCacheValid(dropdownCache.categories)) {
      return { data: dropdownCache.categories.data };
    }

    const response = await api.get('/api/v1/categories/lite');
    dropdownCache.categories.data = response.data;
    dropdownCache.categories.timestamp = Date.now();
    return response;
  }
};

export const suppliers = {
  // Get all suppliers (full data)
  getAll: () => api.get('/api/v1/suppliers'),

  // Get lightweight suppliers for dropdowns (cached)
  getLite: async () => {
    if (isCacheValid(dropdownCache.suppliers)) {
      return { data: dropdownCache.suppliers.data };
    }

    const response = await api.get('/api/v1/suppliers/lite');
    dropdownCache.suppliers.data = response.data;
    dropdownCache.suppliers.timestamp = Date.now();
    return response;
  }
};

// Function to clear dropdown cache (useful after creating new brands/categories/suppliers)
export const clearDropdownCache = () => {
  dropdownCache.brands.data = null;
  dropdownCache.brands.timestamp = null;
  dropdownCache.categories.data = null;
  dropdownCache.categories.timestamp = null;
  dropdownCache.suppliers.data = null;
  dropdownCache.suppliers.timestamp = null;
};

// Activities endpoints
export const activities = {
  getStockMovements: (params) => api.get('/api/v1/activities/stock-movements', { params }),
  getLogs: (params) => api.get('/api/v1/activities/logs', { params }),
};

// Admin endpoints (admin only)
export const admin = {
  bulkDeleteProductInventory: (confirmation) =>
    api.post('/api/v1/admin/bulk-delete-product-inventory', { confirmation }),
  bulkDeleteQuaipoLookup: (confirmation) =>
    api.post('/api/v1/admin/bulk-delete-quaipo-lookup', { confirmation }),
};

export default api;